/**
 * MODALE AZIONE (blocco ALLORA) — sceglie l'azione dal catalogo e ne compone il titolo.
 *
 * Aperta dal builder in due modi, con lo stesso componente: **aggiunta** (`action` assente) e
 * **modifica** (`action` valorizzata). Non è una scelta di comodo: la parte che conta — titolo,
 * politica di errore, configurazione del tipo — è la stessa, e due componenti gemelli
 * divergerebbero alla prima modifica fatta su uno solo.
 *
 * Differiscono per una cosa sola: in modifica **il catalogo non si mostra**. L'azione è già scelta,
 * ci si è arrivati da una riga esistente, e rimettere davanti l'elenco inviterebbe a rifare una
 * scelta già fatta — con l'aggravante che sceglierne un'altra azzera la configurazione appena
 * aperta. Il tipo si cambia togliendo la riga e rimettendola, che è anche ciò che accade davvero.
 *
 * Non parla col server e non tocca lo store: riceve l'azione, restituisce l'azione. Chi l'ha
 * aperta decide cosa farne (il builder la mette nel suo stato locale, che al salvataggio
 * diventerà payload). Una modale che salvasse da sé non potrebbe servire una bozza in composizione.
 *
 * ## Perché una modale e non una combo
 *
 * Qui dentro sta il **form di configurazione specifico di ogni azione**: quello scritto a mano che
 * il registry dà per il codice, altrimenti il form generico dei parametri che l'azione dichiara nel
 * catalogo. Un invio mail vuole oggetto e corpo coi placeholder, una creazione attività vuole
 * altro. Una tendina non avrebbe potuto ospitarlo, ed è la ragione per cui questa modale sostituisce
 * il `ChipSelect` che faceva da palette.
 *
 * ## Ricerca (solo in aggiunta)
 *
 * Filtro CLIENT-SIDE, senza rete: le azioni arrivano già tutte come prop, caricate dal builder al
 * cambio oggetto (a differenza di `AddRelationDrawer`, che cerca entità sul server e per questo usa
 * `useServerPagedSearch`). L'ordinamento è alfabetico per etichetta localizzata e sta qui, come per
 * oggetti ed eventi del QUANDO: dipende dalla locale, che conosce il client.
 *
 * ## Cosa NON si cerca qui
 *
 * L'elenco è già filtrato dal server per l'entità del QUANDO: un'azione inapplicabile — aggiornare
 * un campo su un'entità che non ne dichiara di scrivibili — non arriva proprio. La modale non
 * ricontrolla e non deve: il vincolo lo conosce l'azione lato Java, non il client.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useActionConfigForm, DeclaredParamsActionConfig } from './actions/automationActionConfig';
import { ChipSelect } from './catalogFieldEditors';
import { actionIcon, DEFAULT_ACTION_ICON } from './automationIcons';
import { ACTION_ON_ERROR_OPTIONS } from './automationTypes';
import type {
  AutomationActionOnError,
  AutomationActionType,
  AutomationParam,
} from './automationTypes';

/** Nessun parametro dichiarato: riferimento stabile, per non crearne uno nuovo a ogni render. */
const NO_PARAMS: AutomationParam[] = [];

/**
 * Ciò che la modale riceve e restituisce: l'azione senza l'id di riga, che è del builder.
 *
 * Sono i campi COMUNI a ogni azione — titolo e politica di errore. La configurazione specifica del
 * tipo (TS-65) non è qui: quella dipende da cosa l'azione fa, questi no.
 */
export interface AutomationActionDraft {
  code: string;
  title: string;
  /**
   * Configurazione specifica del tipo, nella forma che il suo form dichiara — opaca per la modale,
   * che la trasporta e basta. `null` per un'azione appena aggiunta, o per un'azione che una
   * configurazione non ce l'ha.
   */
  config: Record<string, unknown> | null;
  onError: AutomationActionOnError;
  retryCount: number;
  retryDelaySeconds: number;
}

interface AutomationActionModalProps {
  /** L'azione da modificare. Assente = si sta aggiungendo. */
  action?: AutomationActionDraft;
  /**
   * FQN dell'entità scelta nel blocco QUANDO. La modale non la usa: la passa al form di
   * configurazione, i cui vocabolari dipendono dall'entità.
   */
  className: string;
  /**
   * Azioni offribili per l'entità scelta nel QUANDO, già filtrate dal server per applicabilità. Le
   * riceve invece di leggerle dallo store perché quale sia l'entità lo sa il builder, non lei — e
   * così la modale torna a essere ciò che il suo commento di testata dichiara: riceve e restituisce,
   * senza toccare nulla intorno.
   */
  actionTypes: AutomationActionType[];
  /**
   * Consultazione: la modale mostra la configurazione ma non la lascia toccare. Serve alle view in
   * sola lettura del builder, dove aprire un'azione è l'unico modo per vederne la configurazione —
   * la riga da sola dice titolo, tipo e politica di errore, non il contenuto.
   */
  readOnly?: boolean;
  onClose: () => void;
  onConfirm: (action: AutomationActionDraft) => void;
}

export default function AutomationActionModal({
  action,
  actionTypes,
  className,
  readOnly = false,
  onClose,
  onConfirm,
}: AutomationActionModalProps) {
  const { t } = useTranslation();

  // Aggiunta o modifica: la modale è una sola, ma non presenta le stesse cose. In modifica il tipo
  // è già scelto — ci si è arrivati da una riga esistente — quindi il catalogo non si mostra.
  const isEditing = Boolean(action);

  const [query, setQuery] = useState('');
  const [code, setCode] = useState(action?.code ?? '');
  const [title, setTitle] = useState(action?.title ?? '');
  // Opaca anche qui: la modale non sa cosa contenga, la tiene e la restituisce. A conoscerne la
  // forma sono solo i due capi — il form che la compone e il provider Java che la valida.
  const [config, setConfig] = useState<Record<string, unknown> | null>(action?.config ?? null);
  // Nasce su STOP, come la combo sorgente del SE nasce su 'STATIC': un enum a set chiuso che quasi
  // nessuno cambia parte dal valore sensato invece che vuoto. STOP e' il conservativo — davanti a un
  // errore la catena si ferma, e chi legge il log vede dove.
  const [onError, setOnError] = useState<AutomationActionOnError>(action?.onError ?? 'STOP');
  const [retryCount, setRetryCount] = useState(action?.retryCount ?? 0);
  const [retryDelaySeconds, setRetryDelaySeconds] = useState(action?.retryDelaySeconds ?? 0);

  /**
   * Le azioni che corrispondono a ciò che si sta cercando, in ordine alfabetico.
   * <p>Si cerca nel nome E nella DESCRIZIONE, perché la descrizione è dove stanno le parole con cui
   * uno pensa l'azione: «Crea un'attività assegnata a un utente o a una coda» contiene "coda" e
   * "assegna", che nel nome — «Crea attività» — non compaiono. Cercandole solo nel nome, l'elenco
   * risponde "nessun risultato" mentre la parola cercata sta a video, due righe sotto.
   * <p>La descrizione è facoltativa e il server la omette quando l'azione non la dichiara: il `??`
   * tratta l'assenza come "non corrisponde", non come un errore.
   * <p>L'ago si prepara UNA volta e non a ogni voce: con tre azioni non cambia niente, ma questo
   * elenco è pensato per crescere ed è la prima cosa che si sarebbe ripetuta per venti.
   * <p><b>Prima chi corrisponde nel NOME</b>, poi chi corrisponde solo nella descrizione, e dentro
   * i due gruppi in ordine alfabetico. Senza questa distinzione un'azione trovata per una parola
   * della descrizione potrebbe precedere alfabeticamente quella che si chiama proprio come si è
   * digitato — chi cerca "email" si aspetta «Invia email» in cima, non a metà elenco.
   * <p>A ricerca VUOTA i due gruppi coincidono: `includes('')` è vero per tutti, quindi l'elenco
   * resta quello alfabetico di sempre e nessuna azione guadagna una posizione per caso.
   */
  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matchesLabel = (a: AutomationActionType) => a.label.toLowerCase().includes(needle);

    return actionTypes
      .filter(
        (a) => matchesLabel(a) || (a.description?.toLowerCase().includes(needle) ?? false),
      )
      .sort((x, y) => {
        if (matchesLabel(x) !== matchesLabel(y)) return matchesLabel(x) ? -1 : 1;

        return x.label.localeCompare(y.label);
      });
  }, [actionTypes, query]);

  /**
   * Scegliere un'azione RIMPIAZZA sempre il titolo con la sua etichetta, anche se l'utente l'aveva
   * già cambiato. Il "pre-riempi solo se non l'ha toccato" sarebbe più gentile ma imprevedibile:
   * l'utente non sa che il campo ha memoria. Così la regola è una sola e si spiega in una riga —
   * scegli un'azione, ottieni il suo titolo di default; poi è tuo.
   */
  const selectAction = (next: string) => {
    setCode(next);
    setTitle(actionTypes.find((a) => a.code === next)?.label ?? '');
    // La config appartiene al TIPO: cambiare azione la azzera, perché le chiavi della precedente
    // non significano niente per la nuova — e sopravvivendo arriverebbero al server come chiavi
    // ignote che il provider giudicherebbe assurde.
    setConfig(null);
  };

  // Il form della config, se quell'azione ne ha uno. La modale non sa quale sia né cosa mostri:
  // interroga il registry per codice (plugin abilitati, poi core), e con `null` semplicemente non c'è nulla sotto il titolo.
  const ConfigForm = useActionConfigForm(code);

  // La voce di catalogo dell'azione scelta: serve in MODIFICA, dove l'elenco non c'è e senza di
  // essa la modale non direbbe di quale azione si sta cambiando la configurazione — il titolo è
  // testo dell'utente e può non dirlo ("Qualifica il lead"). Assente se il codice non è più
  // registrato (plugin spento, azione ritirata): in quel caso non si mostra nulla, invece di
  // inventare un nome.
  const selectedActionType = actionTypes.find((a) => a.code === code);

  // Senza un form scritto a mano, i parametri che l'azione dichiara nel catalogo: il form generico
  // li disegna con lo stesso campo del QUANDO. `params` è sempre nel payload del catalogo (il
  // converter lo scrive anche vuoto): il `?? NO_PARAMS` copre solo l'azione non trovata nel
  // catalogo (plugin spento), che non ha una voce da cui leggerli.
  const declaredParams = ConfigForm ? NO_PARAMS : (selectedActionType?.params ?? NO_PARAMS);
  const SelectedIcon = actionIcon(selectedActionType?.icon || DEFAULT_ACTION_ICON);

  // Il titolo è obbligatorio (AutomationAction.title lo è lato server): senza, non si conferma.
  const canConfirm = Boolean(code) && title.trim().length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {/* Tre casi, non due: in consultazione non si modifica nulla, e chiamarla "Modifica
              azione" prometterebbe ciò che la modale non concede. */}
          {t(
            readOnly
              ? 'automation.form.viewAction'
              : action
                ? 'automation.form.editAction'
                : 'automation.form.addAction',
          )}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="app-dialog__body">
        {/* In modifica, l'azione che si sta configurando: stessa lettura di una voce dell'elenco —
            icona, nome, effetto — ma inerte, perché non c'è niente da scegliere. In aggiunta non
            serve: l'elenco è lì sotto e la voce scelta è evidenziata. */}
        {isEditing && selectedActionType && (
          <div className="action-chosen">
            <span
              className={`builder-arow__icon builder-arow__icon--${selectedActionType.icon || DEFAULT_ACTION_ICON}`}
            >
              <SelectedIcon />
            </span>
            <span className="action-picker__text">
              <span className="action-picker__label">{selectedActionType.label}</span>
              {selectedActionType.description && (
                <span className="action-picker__description">{selectedActionType.description}</span>
              )}
            </span>
          </div>
        )}

        {/* Ricerca ed elenco solo in AGGIUNTA. In modifica l'azione è già scelta e si è arrivati qui
            per cambiarne la configurazione: mostrare il catalogo inviterebbe a rifare una scelta già
            fatta, e sceglierne un'altra azzererebbe la config appena aperta. Il tipo si cambia
            togliendo la riga e rimettendola, che è anche ciò che accade davvero. */}
        {!isEditing && (
          <TextField
            placeholder={t('common.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
            size="small"
            className="form-field"
            // Lo scopo primario della modale in aggiunta è cercare: come in AddRelationDrawer, si
            // parte già qui.
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        {!isEditing && (
        <div className="action-picker">
          {options.length === 0 && <div className="action-picker__empty">{t('common.noResults')}</div>}
          {options.map((a) => {
            const Icon = actionIcon(a.icon || DEFAULT_ACTION_ICON);

            return (
              <button
                type="button"
                key={a.code}
                className={`action-picker__item${a.code === code ? ' action-picker__item--on' : ''}`}
                onClick={() => selectAction(a.code)}
              >
                {/* Come nelle righe del builder: la variante segue il token, e se lo SCSS non ha
                    una regola per quel token resta lo stile di base. */}
                <span className={`builder-arow__icon builder-arow__icon--${a.icon || DEFAULT_ACTION_ICON}`}>
                  <Icon />
                </span>
                {/* Nome ed effetto in colonna: il primo dice come si chiama l'azione, la seconda
                    cosa fa. La descrizione è facoltativa e il server la OMETTE quando manca, quindi
                    si rende l'elemento solo se c'è testo — una riga vuota sotto il nome
                    scomporrebbe l'allineamento dell'elenco senza dire nulla. */}
                <span className="action-picker__text">
                  <span className="action-picker__label">{a.label}</span>
                  {a.description && (
                    <span className="action-picker__description">{a.description}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        )}

        {/* In aggiunta compare solo a azione scelta: prima non ci sarebbe nulla da pre-riempire, e un
            campo vuoto in cima chiederebbe all'utente di inventarsi un nome prima di sapere di cosa.
            Staccato dalla lista da un separatore, perché sono due momenti distinti — prima si
            sceglie, poi si configura. In MODIFICA la lista non c'è, quindi non c'è nemmeno niente da
            separare: il blocco è l'intero corpo della modale. In fondo si innesta il form specifico
            dell'azione. */}
        {code && (
          // In consultazione l'intero blocco è un `<fieldset disabled>`, come il corpo del builder:
          // spegne in un colpo solo tutti i CONTROLLI DI FORM al suo interno, compresi quelli di un
          // form di configurazione che questa modale non conosce.
          //
          // E basta davvero, da quando le scelte si fanno con `ChipSelect` invece che con la Select
          // di MUI: quest'ultima mostra un `div` con `tabindex` e tiene l'input nascosto, quindi il
          // fieldset non la fermava e serviva un `disabled` esplicito su ognuna. `ChipSelect` è un
          // `<button>` vero e non ha quel problema — per questo il contratto del form di
          // configurazione non porta nessun flag di sola lettura.
          <fieldset
            disabled={readOnly}
            className={`action-config${isEditing ? ' action-config--alone' : ''}`}
          >
            {/* PARAMETRI COMUNI a ogni azione. Raggruppati e riquadrati per distinguerli da quelli
                del tipo scelto: prima erano fratelli impilati del form specifico, e nulla diceva
                che titolo e politica di errore valgono per QUALUNQUE azione mentre il resto no.
                Stessa forma dei gruppi di condizione del blocco SE — riquadro con etichettina
                sopra — perché è lo stesso concetto, cose che stanno insieme sotto un nome. */}
            <div className="action-group">
              <span className="action-group__label">{t('automation.form.commonSettings')}</span>

              {/* Titolo in stile builder e non con la TextField di MUI: era l'unico campo con
                  label flottante in un pannello fatto di chip e riquadri, e due linguaggi visivi
                  nello stesso posto si notano. Il `required` di MUI era comunque decorativo — a
                  impedire il salvataggio senza titolo è `canConfirm`. */}
              <div className="builder-crow">
                <span className="text-sm text-muted">{t('automation.form.actionTitle')}</span>
                <span className="builder-fieldnum builder-fieldnum--grow">
                  <input
                    type="text"
                    value={title}
                    aria-label={t('automation.form.actionTitle')}
                    onChange={(e) => setTitle(e.target.value)}
                    // In modifica il fuoco andrebbe perso: la ricerca, che in aggiunta se lo
                    // prende, qui non è renderizzata. In consultazione no: il campo è dentro il
                    // fieldset disabilitato e un input disabilitato il fuoco non lo prende —
                    // chiederlo sarebbe dichiarare un'intenzione che non si realizza.
                    autoFocus={isEditing && !readOnly}
                  />
                </span>
              </div>

              {/* Politica di errore e retry su UNA riga, con la stessa resa del blocco SE: chip per
                  la scelta, riquadri numerici per i valori. I due campi di retry compaiono solo con
                  RETRY — altrimenti chiederebbero di ignorare input inerti.

                  `ChipSelect` e gli `<input>` sono controlli di form veri, quindi in consultazione
                  li spegne il fieldset qui sopra: nessun `disabled` da passare a mano. */}
              <div className="builder-crow">
                <span className="text-sm text-muted">{t('automation.form.onError')}</span>
                <ChipSelect
                  value={onError}
                  options={ACTION_ON_ERROR_OPTIONS.map((o) => ({
                    value: o.value,
                    label: t(o.label),
                  }))}
                  // L'etichetta accanto è solo visiva: nulla la lega al bottone, che senza questa
                  // si annuncerebbe "Ritenta" e basta.
                  ariaLabel={t('automation.form.onError')}
                  onChange={(next) => setOnError(next as AutomationActionOnError)}
                />

                {onError === 'RETRY' && (
                  <>
                    <span className="builder-fieldnum">
                      {t('automation.form.retryCount')}{' '}
                      <input
                        type="number"
                        min={0}
                        value={retryCount}
                        aria-label={t('automation.form.retryCount')}
                        onChange={(e) => setRetryCount(Number(e.target.value) || 0)}
                      />
                    </span>
                    <span className="builder-fieldnum">
                      {t('automation.form.retryDelaySeconds')}{' '}
                      <input
                        type="number"
                        min={0}
                        value={retryDelaySeconds}
                        aria-label={t('automation.form.retryDelaySeconds')}
                        onChange={(e) => setRetryDelaySeconds(Number(e.target.value) || 0)}
                      />
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Configurazione specifica del tipo, nel suo riquadro e sotto il NOME dell'azione: chi
                guarda vede di chi sono quei campi senza doverlo dedurre. La modale non sa cosa
                siano — monta ciò che il registry le dà per quel codice, e gli passa l'entità del
                QUANDO perché i vocabolari che consuma dipendono da lei.
                Il riquadro compare solo se un form c'è: un'azione senza configurazione — o
                registrata da un modulo che il core non conosce — lascerebbe altrimenti un gruppo
                intestato e vuoto, che promette campi che non arriveranno. */}
            {ConfigForm && (
              <div className="action-group">
                <span className="action-group__label">
                  {selectedActionType?.label ?? t('automation.form.actionConfig')}
                </span>

                {/* `key={code}`: un form di configurazione può tenere stato locale, e due codici
                    azione potrebbero un giorno mappare sullo STESSO componente. Senza la key React
                    riuserebbe l'istanza al cambio azione — la config viene azzerata da
                    selectAction, lo stato interno no, e input e config direbbero cose diverse. Con
                    la key il rimontaggio è garantito dal codice, non dalla coincidenza che i
                    componenti differiscano. */}
                <ConfigForm
                  key={code}
                  className={className}
                  config={config}
                  configContext={selectedActionType?.configContext}
                  onChange={setConfig}
                />
              </div>
            )}

            {/* Nessun form scritto a mano, ma l'azione DICHIARA i suoi parametri: il form generico,
                nello stesso riquadro. Dentro il fieldset come tutto il resto, quindi la sola
                lettura vale anche qui senza flag. La `key` per la stessa ragione del form sopra. */}
            {declaredParams.length > 0 && (
              <div className="action-group">
                <span className="action-group__label">
                  {selectedActionType?.label ?? t('automation.form.actionConfig')}
                </span>
                <DeclaredParamsActionConfig
                  key={code}
                  params={declaredParams}
                  config={config}
                  onChange={setConfig}
                />
              </div>
            )}

            {/* Azione che il catalogo dichiara SENZA parametri: lo stesso riquadro, ma con una riga
                che lo dice, perché il vuoto sotto il titolo si leggerebbe come un form che non si è
                caricato. Solo su `false` esplicito: un'azione assente dal catalogo (plugin spento)
                resta senza riquadro, come prima. */}
            {!ConfigForm && declaredParams.length === 0 &&
              selectedActionType?.hasConfiguration === false && (
              <div className="action-group">
                <span className="action-group__label">{selectedActionType.label}</span>
                <span className="text-sm text-muted">
                  {t('automation.form.actionHasNoConfiguration')}
                </span>
              </div>
            )}
          </fieldset>
        )}
      </DialogContent>

      {/* In consultazione non c'è niente da confermare: un solo pulsante, e dice "chiudi" invece di
          "annulla" — non si sta abbandonando nulla. */}
      <DialogActions className="app-dialog__footer">
        <Button onClick={onClose}>{t(readOnly ? 'common.close' : 'common.cancel')}</Button>
        {!readOnly && (
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={() =>
            onConfirm({
              code,
              title: title.trim(),
              config,
              onError,
              // I retry hanno senso solo con RETRY: su STOP/CONTINUE si azzerano invece di
              // portarsi dietro un valore che l'utente aveva digitato e poi abbandonato.
              retryCount: onError === 'RETRY' ? retryCount : 0,
              retryDelaySeconds: onError === 'RETRY' ? retryDelaySeconds : 0,
            })
          }
        >
          {t('common.confirm')}
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
